import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  HStack,
  Text,
  Badge,
  Button,
  SimpleGrid,
  Spinner,
  Alert,
  AlertIcon,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import UserSettingsLayout from './UserSettingsLayout';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import McpModal from './McpModal';
import ConnectedToolsList from './client/ConnectedToolsList';
import { CLIENT_TOOLS, clientIconSrc, clientLabel } from './client/clientTools';
import { getConnectorLogo } from '../utils/connectorLogos';
import { sortConnectorsByAvailability } from '../utils/connectorSorting';
import notionConnector      from '../tools/connectors/notion';
import googleDriveConnector from '../tools/connectors/google-drive';
import amplitudeConnector   from '../tools/connectors/amplitude';
import figmaConnector       from '../tools/connectors/figma';
import miroConnector        from '../tools/connectors/miro';
import askableConnector     from '../tools/connectors/askable';
import ideallyConnector     from '../tools/connectors/ideally';
import granolaConnector     from '../tools/connectors/granola';
import githubConnector      from '../tools/connectors/github';

// Catalog built from the connector modules — no duplication.
// Adding a new connector = create src/tools/connectors/<id>.js, import it here.
const CONNECTOR_CATALOG = Object.fromEntries(
  [notionConnector, googleDriveConnector, amplitudeConnector, figmaConnector, miroConnector, askableConnector, ideallyConnector, granolaConnector, githubConnector]
    .map(c => [c.id, {
      tagline: c.tagline,
      description: c.description,
      useCases: c.useCases,
      accountGuidance: c.accountGuidance,
    }])
);

// ── Shared: connector detail modal ───────────────────────────────────────────
const ConnectorDetailModal = ({ connector, isOpen, onClose, onConnect, onDisconnect, actionLoading }) => {
  if (!connector) return null;
  const catalog = CONNECTOR_CATALOG[connector.id] || {};
  const logoEntry = getConnectorLogo(connector.id);
  const isConnected = connector.status === 'connected';
  const isLoading   = actionLoading === connector.id;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="16px" overflow="hidden">
        <ModalCloseButton color="gray.400" />
        <ModalBody p={0}>
          <Box p={6}>
            <HStack spacing={4} mb={4} align="flex-start">
              <Box
                w="48px" h="48px" borderRadius="12px"
                bg={logoEntry ? logoEntry.bg : (connector.iconBg || '#888')}
                display="flex" alignItems="center" justifyContent="center" flexShrink={0}
              >
                {logoEntry
                  ? <logoEntry.Logo size={24} />
                  : <Text fontSize="20px" color="white" fontWeight="800" lineHeight={1}>{connector.icon}</Text>
                }
              </Box>
              <Box flex="1">
                <HStack mb={1}>
                  <Text fontWeight="700" fontSize="md" color="gray.900">{connector.name}</Text>
                  {isConnected && (
                    <Badge colorScheme="green" fontSize="xs" borderRadius="full" px={2}>Connected</Badge>
                  )}
                </HStack>
                <Text fontSize="sm" color="gray.500" lineHeight="1.5">
                  {catalog.tagline || connector.description}
                </Text>
              </Box>
            </HStack>

            {catalog.description && (
              <Text fontSize="sm" color="gray.600" lineHeight="1.7" mb={4}>
                {catalog.description}
              </Text>
            )}

            {catalog.useCases?.length > 0 && (
              <Box mb={5}>
                <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wider" color="gray.400" mb={2}>
                  Use cases
                </Text>
                <List spacing={1}>
                  {catalog.useCases.map((uc, i) => (
                    <ListItem key={i} display="flex" alignItems="flex-start">
                      <ListIcon as={CheckCircleIcon} color="green.400" mt="3px" boxSize={3} />
                      <Text fontSize="sm" color="gray.700">{uc}</Text>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {catalog.accountGuidance && (
              <Box bg="orange.50" borderRadius="lg" px={3} py={2.5} mb={5}>
                <Text fontSize="xs" color="orange.800" lineHeight="1.6">
                  {catalog.accountGuidance}
                </Text>
              </Box>
            )}

            {isConnected ? (
              <Flex gap={3}>
                <Button
                  size="sm" variant="outline" colorScheme="red"
                  onClick={() => { onDisconnect(connector.id); onClose(); }}
                  isLoading={isLoading}
                >
                  Disconnect
                </Button>
                {connector.last_used_at && (
                  <Text fontSize="xs" color="gray.400" alignSelf="center">
                    Last used {new Date(connector.last_used_at).toLocaleDateString()}
                  </Text>
                )}
              </Flex>
            ) : (
              <Button
                size="sm" colorScheme="brand" w="100%"
                onClick={() => onConnect(connector.id)}
                isLoading={isLoading}
              >
                Connect to {connector.name}
              </Button>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// ── Landing page ──────────────────────────────────────────────────────────────
const ConnectorsPage = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const params    = new URLSearchParams(location.search);
  const justConnected = Boolean(params.get('connected'));
  const connectError  = params.get('error');

  const [connectors, setConnectors] = useState([]);
  const [mcpModalOpen, setMcpModalOpen] = useState(false);
  const aiTools = useAiToolConnections();

  useEffect(() => {
    fetchWithAuth('/api/connectors')
      .then(r => r.json())
      .then(data => { if (data.connectors) setConnectors(data.connectors); })
      .catch(() => {});
  }, []);

  const myConnections = connectors.filter(c => c.id !== 'web_search' && c.status === 'connected');
  const toolNames = [...new Set(aiTools.connections.map(c => clientLabel(c.client_name)))];

  return (
    <UserSettingsLayout>
      <Box width="100%" bg="white" px={{ base: 5, md: 10 }} py={8} maxW="760px">

        {justConnected && (
          <Alert status="success" borderRadius="lg" mb={6}>
            <AlertIcon />
            Connected successfully.
          </Alert>
        )}
        {connectError && (
          <Alert status="error" borderRadius="lg" mb={6}>
            <AlertIcon />
            {connectError === 'token_exchange_failed'
              ? 'Connection failed. Pabs could not complete authorization. Please try again.'
              : connectError === 'oauth_session_expired'
                ? 'The connection session expired. Please start the connection again.'
              : `Connection error: ${connectError}`}
          </Alert>
        )}

        <Box mb={8}>
          <Text fontWeight="700" fontSize="xl" color="gray.900">Connections</Text>
          <Text fontSize="sm" color="gray.700" mt={1}>Manage and explore connections</Text>
        </Box>

        {/* My connections table: sources Pabs reads and AI tools connected to Pabs */}
        {(myConnections.length > 0 || aiTools.connections.length > 0) && (
          <Box mb={8}>
            <Text fontWeight="600" fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={3}>
              My connections
            </Text>
            <Box border="1px solid" borderColor="gray.200" borderRadius="12px" overflow="hidden">
              <Flex px={4} py={2} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
                <Text flex="1" fontSize="xs" fontWeight="600" color="gray.500">Connection</Text>
                <Text fontSize="xs" fontWeight="600" color="gray.500">Access</Text>
              </Flex>
              {myConnections.map((c, i) => {
                const logoEntry = getConnectorLogo(c.id);
                return (
                <Flex
                  key={c.id}
                  align="center" justify="space-between"
                  px={4} py={3}
                  borderBottom={i < myConnections.length - 1 ? '1px solid' : 'none'}
                  borderColor="gray.100"
                >
                  <HStack spacing={3}>
                    <Box
                      w="28px" h="28px" borderRadius="6px"
                      bg={logoEntry ? logoEntry.bg : (c.iconBg || '#888')}
                      display="flex" alignItems="center" justifyContent="center" flexShrink={0}
                    >
                      {logoEntry
                        ? <logoEntry.Logo size={14} />
                        : <Text fontSize="11px" color="white" fontWeight="800">{c.icon}</Text>
                      }
                    </Box>
                    <Text fontSize="sm" fontWeight="500" color="gray.800">{c.name}</Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Text fontSize="sm" color="gray.600">Can view and search content</Text>
                    <Text fontSize="sm" color="gray.400">···</Text>
                  </HStack>
                </Flex>
              );
              })}
              {aiTools.connections.map((c, i) => {
                const tool = CLIENT_TOOLS.find(item => item.id === c.client_name);
                return (
                  <Flex
                    key={c.id}
                    align="center" justify="space-between"
                    px={4} py={3} cursor="pointer" _hover={{ bg: 'gray.50' }}
                    borderTop={myConnections.length > 0 || i > 0 ? '1px solid' : 'none'}
                    borderColor="gray.100"
                    onClick={() => navigate('/connectors/ai-tools')}
                  >
                    <HStack spacing={3}>
                      <Box w="28px" h="28px" borderRadius="6px" border="1px solid" borderColor="gray.200" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                        {tool && <img src={clientIconSrc(tool)} alt="" width="16" height="16" style={{ width: 16, height: 16, objectFit: 'contain' }} />}
                      </Box>
                      <Text fontSize="sm" fontWeight="500" color="gray.800">{clientLabel(c.client_name)}</Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.600" noOfLines={1} maxW="60%">Works with {c.plan_name || 'your plan'}</Text>
                  </Flex>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Explore — two clean text-forward cards */}
        <Box mb={2}>
          <Text fontWeight="600" fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={3}>
            Explore connections
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>

            <Box
              border="1px solid" borderColor="gray.200" borderRadius="12px"
              p={6} cursor="pointer"
              _hover={{ borderColor: 'gray.400' }} transition="border-color 0.15s"
              onClick={() => navigate('/connectors/knowledge')}
            >
              <Text fontWeight="700" fontSize="md" color="gray.900" mb={2}>Knowledge Sources</Text>
              <Text fontSize="sm" color="gray.700" lineHeight="1.6" mb={5}>
                Let Pabs work from your real docs, research, meetings, code and designs.
              </Text>
              <Text fontSize="sm" fontWeight="600" color="gray.900">Explore →</Text>
            </Box>

            <Box
              border="1px solid" borderColor="gray.200" borderRadius="12px"
              p={6} cursor="pointer"
              _hover={{ borderColor: 'gray.400' }} transition="border-color 0.15s"
              onClick={() => (toolNames.length ? navigate('/connectors/ai-tools') : setMcpModalOpen(true))}
            >
              <Text fontWeight="700" fontSize="md" color="gray.900" mb={2}>AI tools</Text>
              <Text fontSize="sm" color="gray.700" lineHeight="1.6" mb={5}>
                {toolNames.length
                  ? `Pabs is connected to ${toolNames.length > 1 ? `${toolNames.slice(0, -1).join(', ')} and ${toolNames[toolNames.length - 1]}` : toolNames[0]}. See which plans they work with, or connect another tool.`
                  : 'Connect Pabs to the AI tools you build with, so strategic judgment stays active while you ship.'}
              </Text>
              <Text fontSize="sm" fontWeight="600" color="gray.900">{toolNames.length ? 'Manage →' : 'Connect →'}</Text>
            </Box>

          </SimpleGrid>
        </Box>

      </Box>

      <McpModal
        isOpen={mcpModalOpen}
        onClose={() => setMcpModalOpen(false)}
        currentPlanId={null}
        planName="PABS"
      />
    </UserSettingsLayout>
  );
};

// AI tools connected to any of the account's plans.
function useAiToolConnections() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    Promise.all(['/api/client-setup/connections','/api/plugins/connections'].map(url => fetchWithAuth(url)
      .then(response => (response.ok ? response.json() : {connections:[]})).catch(() => ({connections:[]}))))
      .then(([installed,plugins]) => { if (!cancelled) setConnections([...(installed.connections || []),
        ...(plugins.connections || []).map(connection => ({...connection,display_name:connection.client_name,client_name:'pabs-plugin',kind:'plugin'}))]); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  return { connections, setConnections, loading };
}

// ── AI tools child page ───────────────────────────────────────────────────────
export const AiToolsPage = () => {
  const navigate = useNavigate();
  const { connections, setConnections, loading } = useAiToolConnections();
  const [mcpModalOpen, setMcpModalOpen] = useState(false);
  return (
    <UserSettingsLayout>
      <Box width="100%" bg="white" px={{ base: 5, md: 10 }} py={8} maxW="760px">
        <Button
          variant="ghost" size="sm" px={0} mb={6} color="gray.600"
          leftIcon={<span className="material-icons-outlined" style={{ fontSize: '16px' }}>arrow_back</span>}
          onClick={() => navigate('/connectors')}
          _hover={{ color: 'gray.900', bg: 'transparent' }}
        >
          Connections
        </Button>
        <Flex mb={8} align="flex-start" justify="space-between" gap={4} wrap="wrap">
          <Box>
            <Text fontWeight="700" fontSize="xl" color="gray.900">AI tools</Text>
            <Text fontSize="sm" color="gray.700" mt={1}>
              The AI tools you build with that work with Pabs, and the plan each one uses.
            </Text>
          </Box>
          <Button size="sm" variant="outline" onClick={() => setMcpModalOpen(true)}>Connect another tool</Button>
        </Flex>
        {loading ? (
          <Box py={6}><Spinner size="sm" color="gray.400" /></Box>
        ) : connections.length ? (
          <>
            <ConnectedToolsList
              connections={connections}
              showPlan
              onDisconnected={(id) => setConnections(current => current.filter(item => item.id !== id))}
            />
            <Text fontSize="xs" color="gray.600" mt={3} lineHeight="1.6">
              Disconnecting stops that tool’s access. Your plan stays as it is. Also remove Pabs from the tool’s own connector or install settings.
            </Text>
          </>
        ) : (
          <Text fontSize="sm" color="gray.600">No AI tools are connected yet.</Text>
        )}
      </Box>
      <McpModal isOpen={mcpModalOpen} onClose={() => setMcpModalOpen(false)} currentPlanId={null} planName="PABS" />
    </UserSettingsLayout>
  );
};

// ── Knowledge Sources child page ──────────────────────────────────────────────
export const KnowledgeSourcesPage = () => {
  const navigate  = useNavigate();

  const [connectors, setConnectors]       = useState([]);
  const [loading, setLoading]             = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedConnector, setSelectedConnector] = useState(null);

  const fetchConnectors = useCallback(() => {
    setLoading(true);
    fetchWithAuth('/api/connectors')
      .then(r => r.json())
      .then(data => { if (data.connectors) setConnectors(data.connectors); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchConnectors(); }, [fetchConnectors]);

  const handleConnect    = (id) => {
    const token = localStorage.getItem('token');
    const apiBase = window.location.port === '3000' ? 'http://localhost:3001' : '';
    const connectUrl = `${apiBase}/api/connectors/connect/${id}?token=${encodeURIComponent(token || '')}`;
    window.open(connectUrl, '_blank', 'noopener,noreferrer');
  };
  const handleDisconnect = async (id) => {
    setActionLoading(id);
    try {
      await fetchWithAuth(`/api/connectors/${id}`, { method: 'DELETE' });
      fetchConnectors();
    } catch {} finally { setActionLoading(null); }
  };

  const webSearch        = connectors.find(c => c.id === 'web_search');
  const knowledgeSources = sortConnectorsByAvailability(
    connectors.filter(c => c.id !== 'web_search')
  );

  return (
    <UserSettingsLayout>
      <Box width="100%" bg="white" px={{ base: 5, md: 10 }} py={8} maxW="760px">

        <Button
          variant="ghost" size="sm" px={0} mb={6} color="gray.600"
          leftIcon={<span className="material-icons-outlined" style={{ fontSize: '16px' }}>arrow_back</span>}
          onClick={() => navigate('/connectors')}
          _hover={{ color: 'gray.900', bg: 'transparent' }}
        >
          Connections
        </Button>

        <Box mb={8}>
          <Text fontWeight="700" fontSize="xl" color="gray.900">Knowledge Sources</Text>
          <Text fontSize="sm" color="gray.700" mt={1}>
            Connect the tools where your product work lives. Pabs reads them to ground its answers, Jobs and reviews in your real docs, research, meetings and code, to check delivery progress in your Pulse, and to use your design files and libraries as Design Authority guidance.
          </Text>
        </Box>

        {loading ? (
          <Box py={6}><Spinner size="sm" color="gray.400" /></Box>
        ) : (
          <Box border="1px solid" borderColor="gray.200" borderRadius="12px" overflow="hidden" mb={6}>
            {knowledgeSources.map((c, i) => {
              const isConnected = c.status === 'connected';
              const needsReconnect = c.status === 'reconnect_required';
              const isDisabled  = c.disabled;
              const catalog     = CONNECTOR_CATALOG[c.id] || {};
              const logoEntry   = getConnectorLogo(c.id);
              return (
                <Flex
                  key={c.id}
                  align="center" justify="space-between"
                  px={4} py={3}
                  borderBottom={i < knowledgeSources.length - 1 ? '1px solid' : 'none'}
                  borderColor="gray.100"
                  cursor={isDisabled ? 'default' : 'pointer'}
                  opacity={isDisabled ? 0.5 : 1}
                  _hover={isDisabled ? {} : { bg: 'gray.50' }}
                  onClick={isDisabled ? undefined : () => setSelectedConnector(c)}
                >
                  <HStack spacing={3}>
                    <Box
                      w="32px" h="32px" borderRadius="8px"
                      bg={logoEntry ? logoEntry.bg : (c.iconBg || '#888')}
                      display="flex" alignItems="center" justifyContent="center" flexShrink={0}
                    >
                      {logoEntry
                        ? <logoEntry.Logo size={16} />
                        : <Text fontSize="13px" color="white" fontWeight="800">{c.icon}</Text>
                      }
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="600" color="gray.900">{c.name}</Text>
                      {catalog.tagline && (
                        <Text fontSize="xs" color="gray.600" noOfLines={1}>{catalog.tagline}</Text>
                      )}
                    </Box>
                  </HStack>
                  {isConnected ? (
                    <Badge colorScheme="green" fontSize="xs" borderRadius="full" px={2}>Connected</Badge>
                  ) : needsReconnect ? (
                    <Badge colorScheme="orange" fontSize="xs" borderRadius="full" px={2}>Reconnect</Badge>
                  ) : isDisabled ? (
                    <Badge colorScheme="gray" fontSize="xs" borderRadius="full" px={2} variant="subtle">Coming soon</Badge>
                  ) : (
                    <Box
                      as="span"
                      fontSize="xs" fontWeight="600" color="gray.700"
                      px={3} py={1} borderRadius="md"
                      border="1px solid" borderColor="gray.300"
                      _hover={{ bg: 'gray.50', borderColor: 'gray.500' }}
                      onClick={e => { e.stopPropagation(); handleConnect(c.id); }}
                      cursor="pointer"
                    >
                      Connect
                    </Box>
                  )}
                </Flex>
              );
            })}
          </Box>
        )}

        {/* Web Search — always on, separate from the connectors list */}
        {webSearch && (
          <Box border="1px solid" borderColor="gray.200" borderRadius="12px" p={5}>
            <Flex align="center" justify="space-between">
              <Box>
                <Text fontWeight="600" fontSize="sm" color="gray.900" mb={0.5}>Web Search</Text>
                <Text fontSize="sm" color="gray.700" lineHeight="1.5">
                  Always active. Agents load real-time web results when relevant to your strategy.
                </Text>
              </Box>
              <Box
                w="36px" h="20px" borderRadius="full" bg="green.400"
                display="flex" alignItems="center" px="3px"
                justifyContent="flex-end" flexShrink={0} ml={4}
              >
                <Box w="14px" h="14px" borderRadius="full" bg="white" />
              </Box>
            </Flex>
          </Box>
        )}

      </Box>

      <ConnectorDetailModal
        connector={selectedConnector}
        isOpen={!!selectedConnector}
        onClose={() => setSelectedConnector(null)}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        actionLoading={actionLoading}
      />
    </UserSettingsLayout>
  );
};

export default ConnectorsPage;
